// Locations Frontend Functions Library
// These functions are self-contained and use _shared helpers directly (like Hoomie)

import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/index.js';
import { LocationModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';

const COLLECTION_NAME = 'locations';

// Frontend function: Get all locations
async function getLocations(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME);
    const locations = docs.map((doc) => {
      return { id: doc.$id, model: new LocationModel(doc) };
    });
    return createCMMSView({ data: locations });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get location by ID
async function getLocationById(db, locationId) {
  try {
    const doc = await getDocumentById(db, COLLECTION_NAME, locationId);
    if (!doc) return createCMMSView({ error: 'Location not found' });
    const location = new LocationModel(doc);
    return createCMMSView({ data: { id: doc.$id, model: location } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get locations by company
async function getLocationsByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    const locations = docs.map((doc) => {
      return { id: doc.$id, model: new LocationModel(doc) };
    });

    return createCMMSView({ data: locations });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get active locations
async function getActiveLocations(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'is_active',
      operator: '==',
      value: true,
    });

    const locations = docs.map((doc) => {
      return { id: doc.$id, model: new LocationModel(doc) };
    });

    return createCMMSView({ data: locations });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create new location
async function createLocation(db, locationData) {
  try {
    const location = new LocationModel(locationData);
    const docRef = await addDocToCollection(
      db,
      COLLECTION_NAME,
      location.toJSON()
    );

    return createCMMSView({ data: docRef });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Update location
async function updateLocation(db, locationId, updateData) {
  try {
    // First get the existing location data
    const existingLocation = await getDocumentById(db, COLLECTION_NAME, locationId);
    if (!existingLocation) {
      return createCMMSView({ error: 'Location not found' });
    }

    // Merge existing data with update data
    const mergedData = {
      ...existingLocation,
      ...updateData,
      updated_at: Date.now()
    };

    const location = new LocationModel(mergedData);
    const docRef = await setDocById(
      db,
      COLLECTION_NAME,
      locationId,
      location.toJSON()
    );

    return createCMMSView({ data: { id: locationId, ...location.toJSON() } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Delete location
async function deleteLocation(db, locationId) {
  try {
    await deleteDocByCollectionAndId(db, COLLECTION_NAME, locationId);
    return createCMMSView({ data: { id: locationId, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Export frontend functions
export {
  getLocations,
  getLocationById,
  getLocationsByCompany,
  getActiveLocations,
  createLocation,
  updateLocation,
  deleteLocation,
};
